// SessionRail.js — the visit TIMELINE: chronological left→right scroll
// through the day. The glowing now-line crossing a block IS session
// progress; the line exiting a block is the Pulse's visit-ended trigger
// made visible. Concurrent visits stack into lanes; scroll back to go
// back in time; the Now pill snaps back to live. Wheel input scrubs time
// horizontally while the pointer is over the rail.
// Decisions 6/10: proportional widths with a min width; replaces the card
// rail outright; amber "wrapping up" state in a visit's final minutes.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useComprehendLive } from 'agent/useComprehendLive';
import * as recorderBridge from 'agent/recorderBridge';
import { parseBackendDateMs } from 'agent/time';
import { T, pill } from './liveTheme';

const MIN_BLOCK_PX = 92;
const LANE_HEIGHT = 38;
const LANE_GAP = 5;
const TICKS_HEIGHT = 12;
const DEFAULT_VISIT_MIN = 45; // visits without schedule times get this width
const NOW_ANCHOR = 0.6; // now-line rides at 60% of the viewport while following
const TICK_CANDIDATES_MIN = [1, 2, 5, 15, 30, 60, 120];

const fmtClock = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Resolve each session to a time range: schedule slot first, createdAt fallback. */
const buildEvents = (sessions, schedule) => {
  const slotByConversation = new Map();
  schedule.forEach((slot) =>
    slot.patients.forEach((p) => {
      if (p.conversationId) {
        slotByConversation.set(p.conversationId, { slot, p });
      }
    }),
  );
  return sessions
    .map((s) => {
      const scheduled = slotByConversation.get(s.conversationId);
      const start = scheduled
        ? new Date(scheduled.slot.start).getTime()
        : parseBackendDateMs(s.createdAtMs ?? s.createdAt);
      const patientEnd = scheduled
        ? scheduled.p.end == null
          ? NaN
          : new Date(scheduled.p.end).getTime()
        : NaN;
      const end = scheduled
        ? Number.isNaN(patientEnd)
          ? new Date(scheduled.slot.end).getTime()
          : patientEnd
        : start + DEFAULT_VISIT_MIN * 60 * 1000;
      return Number.isFinite(start) ? { session: s, start, end } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
};

const TimelineBlock = ({
  ev,
  geo,
  nowMs,
  nudgeCount,
  onExpand,
  onToggleRecord,
}) => {
  const { session } = ev;
  const recording = session.recording;
  const finished = (session.status || '').includes('Transcription Complete');
  const active = nowMs >= ev.start && nowMs < ev.end;
  const future = nowMs < ev.start;
  const durMs = ev.end - ev.start;
  // Amber when the now-line approaches the end (5 min, scaled down for
  // short/demo visits so the state still appears).
  const wrapUpMs = Math.min(5 * 60 * 1000, durMs * 0.25);
  const wrappingUp = active && ev.end - nowMs <= wrapUpMs;
  const elapsedPct = active
    ? Math.min(100, ((nowMs - ev.start) / durMs) * 100)
    : 0;

  const border = wrappingUp
    ? '1px solid rgba(251,191,36,0.7)'
    : recording || active
      ? '1px solid rgba(245,54,92,0.55)'
      : future
        ? '1px dashed rgba(17,205,239,0.45)'
        : `1px solid ${T.border}`;

  let statusLine;
  if (active) {
    const inMin = Math.max(0, Math.round((nowMs - ev.start) / 60000));
    const leftMin = Math.max(0, Math.ceil((ev.end - nowMs) / 60000));
    statusLine = wrappingUp
      ? `wrapping up · ${leftMin}m left`
      : `${inMin}m in · ${leftMin}m left`;
  } else if (future) {
    statusLine = fmtClock(ev.start);
  } else {
    statusLine = finished
      ? 'Finished'
      : session.status || `${fmtClock(ev.start)}–${fmtClock(ev.end)}`;
  }

  return (
    <div
      onClick={() => onToggleRecord(session)}
      role="button"
      title={
        recording
          ? 'Click to stop recording this visit'
          : 'Click to start recording this visit'
      }
      style={{
        position: 'absolute',
        left: geo.left,
        top: TICKS_HEIGHT + 6 + geo.lane * (LANE_HEIGHT + LANE_GAP),
        width: geo.width,
        height: LANE_HEIGHT,
        background: active || recording ? T.surfaceCard : T.surfaceMuted,
        border,
        borderRadius: 8,
        cursor: 'pointer',
        opacity: !active && finished ? 0.65 : 1,
        overflow: 'hidden',
        zIndex: active ? 2 : 1,
      }}
    >
      {active && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${elapsedPct}%`,
            background: wrappingUp
              ? 'rgba(251,191,36,0.10)'
              : 'rgba(107,216,136,0.10)',
          }}
        />
      )}
      {nudgeCount > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 1,
            right: 2,
            ...pill('rgba(251,191,36,0.9)', '#412402'),
            fontSize: 8,
            padding: '0 4px',
            zIndex: 2,
          }}
        >
          {nudgeCount}
        </span>
      )}
      <div
        style={{
          position: 'relative',
          height: '100%',
          padding: '0 22px 0 6px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <i
            className="fa fa-microphone"
            aria-hidden="true"
            style={{
              fontSize: 10,
              color: recording ? T.red : 'rgba(159,232,200,0.35)',
              flexShrink: 0,
            }}
          />
          <p
            style={{
              margin: 0,
              flex: 1,
              minWidth: 0,
              fontSize: 11,
              fontWeight: 600,
              color: active || recording ? T.textPrimary : T.textSoft,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {session.patientName || session.name}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {recording && (
            <span
              className="cpl-dot"
              style={{ background: T.red, width: 6, height: 6, flexShrink: 0 }}
            />
          )}
          <p
            style={{
              margin: 0,
              flex: 1,
              minWidth: 0,
              fontSize: 9,
              color: wrappingUp
                ? T.amber
                : recording
                  ? T.redSoft
                  : future
                    ? T.tealSoft
                    : T.textSecondary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {statusLine}
          </p>
          {session.grade && session.grade !== '--' && (
            <span
              style={{
                ...pill(
                  session.grade === 'A'
                    ? 'rgba(45,206,137,0.16)'
                    : session.grade === 'B'
                      ? 'rgba(251,191,36,0.16)'
                      : 'rgba(245,54,92,0.16)',
                  session.grade === 'A'
                    ? T.greenBright
                    : session.grade === 'B'
                      ? T.amber
                      : T.redSoft,
                ),
                fontSize: 9,
                padding: '0 5px',
                flexShrink: 0,
              }}
            >
              {session.grade}
              {active ? ' live' : ''}
            </span>
          )}
        </div>
      </div>
      {/* Full-height details strip — the obvious "more" affordance */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
        role="button"
        aria-label="Visit details"
        title="Patient details"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderLeft: `1px solid ${T.border}`,
          background: 'rgba(255,255,255,0.04)',
          color: T.textSecondary,
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <i
          className="fa fa-chevron-right"
          aria-hidden="true"
          style={{ fontSize: 10 }}
        />
      </div>
    </div>
  );
};

const arrowStyle = (side) => ({
  position: 'absolute',
  [side]: -4,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: 'rgba(13,35,30,0.9)',
  border: `1px solid ${T.border}`,
  color: T.textSoft,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  cursor: 'pointer',
  zIndex: 4,
  padding: 0,
});

const SessionRail = () => {
  const { state, actions } = useComprehendLive();
  const navigate = useNavigate();
  const railRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const [following, setFollowing] = useState(true);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const lastManualScrollRef = useRef(0);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Re-engage live-follow after 30s without the user touching the rail.
  useEffect(() => {
    if (following) return;
    if (Date.now() - lastManualScrollRef.current >= 30 * 1000) {
      setFollowing(true);
    }
  }, [nowMs, following]);

  const todays = state.sessions.filter((s) => s.isToday);
  const visible = todays.length ? todays : state.sessions.slice(-6);

  // ---- Layout: time → pixels ------------------------------------------
  const layout = useMemo(() => {
    const events = buildEvents(visible, state.schedule);
    if (!events.length) return null;

    const durations = events.map((e) => e.end - e.start).sort((a, b) => a - b);
    const medianMs = durations[Math.floor(durations.length / 2)] || 1;
    // Auto-fit: a median-length visit ≈ 120px (works for real 45-min days
    // AND the compressed demo's 105-second slots).
    const pxPerMs = 120 / medianMs;

    const t0 = events[0].start - medianMs * 0.5;
    const tEnd = Math.max(nowMs, ...events.map((e) => e.end)) + medianMs * 1.25;

    // Lane packing by TIME: sequential visits flow horizontally on the
    // same row; only true time-overlaps stack into a new lane. Min-width
    // inflation can visually overlap a few px on short visits — accepted
    // (6/10) over cascading every back-to-back visit into its own lane.
    const laneEnds = []; // end TIME per lane
    const placed = events.map((ev) => {
      const left = (ev.start - t0) * pxPerMs;
      const width = Math.max(MIN_BLOCK_PX, (ev.end - ev.start) * pxPerMs);
      let lane = laneEnds.findIndex((endTime) => endTime <= ev.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = ev.end;
      return { ev, geo: { left, width, lane } };
    });

    // Tick interval: smallest "nice" step that yields ≥70px spacing.
    const tickMin =
      TICK_CANDIDATES_MIN.find((m) => m * 60000 * pxPerMs >= 70) || 120;
    const tickMs = tickMin * 60000;
    const ticks = [];
    for (let t = Math.ceil(t0 / tickMs) * tickMs; t <= tEnd; t += tickMs) {
      ticks.push({ x: (t - t0) * pxPerMs, label: fmtClock(t) });
    }

    return {
      placed,
      ticks,
      t0,
      pxPerMs,
      totalWidth: (tEnd - t0) * pxPerMs,
      lanes: laneEnds.length,
      addVisitLeft:
        Math.max(...placed.map((p) => p.geo.left + p.geo.width)) + 10,
    };
  }, [visible, state.schedule, nowMs]);

  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const updateArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const snapToNow = useCallback(() => {
    const el = railRef.current;
    const l = layoutRef.current;
    if (!el || !l) return;
    const nowX = (Date.now() - l.t0) * l.pxPerMs;
    programmaticScrollRef.current = true;
    el.scrollLeft = Math.max(0, nowX - el.clientWidth * NOW_ANCHOR);
    requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
    updateArrows();
  }, [updateArrows]);

  // Follow the now-line while engaged; manual scroll disengages.
  useEffect(() => {
    if (following) snapToNow();
  }, [following, nowMs, layout, snapToNow]);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    updateArrows();
    // Wheel scrubs time: native non-passive listener (React's synthetic
    // onWheel can't preventDefault reliably).
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const onScroll = () => {
    updateArrows();
    if (!programmaticScrollRef.current) {
      setFollowing(false);
      lastManualScrollRef.current = Date.now();
    }
  };

  const scrollByStep = (dir) => {
    const el = railRef.current;
    if (!el) return;
    setFollowing(false);
    lastManualScrollRef.current = Date.now();
    el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: 'smooth' });
  };

  // Checkbox toggle: add/remove this visit from the multi-record set.
  const toggleRecord = async (session) => {
    const current = recorderBridge.getRecordingIds();
    const next = session.recording
      ? current.filter((id) => id !== session.conversationId)
      : [...new Set([...current, session.conversationId])];
    const rsp = next.length
      ? await recorderBridge.startRecording(next)
      : await recorderBridge.stopRecording(current);
    if (rsp?.error) console.warn('Recorder toggle failed:', rsp.error);
  };

  const nudgesByConversation = state.queue.reduce((acc, q) => {
    if (q.kind === 'nudge' && q.status === 'pending') {
      acc[q.conversationId] = (acc[q.conversationId] || 0) + 1;
    }
    return acc;
  }, {});

  if (!layout) {
    return (
      <p
        style={{
          margin: '12px 0',
          fontSize: 11,
          color: T.textFaint,
          textAlign: 'center',
        }}
      >
        {state.loading ? 'Loading today…' : 'No visits yet today.'}
      </p>
    );
  }

  const nowX = (nowMs - layout.t0) * layout.pxPerMs;
  const railHeight =
    TICKS_HEIGHT + 6 + layout.lanes * (LANE_HEIGHT + LANE_GAP) + 2;

  return (
    <div>
      <div style={{ position: 'relative' }}>
        {canLeft && (
          <button
            onClick={() => scrollByStep(-1)}
            aria-label="Scroll back in time"
            style={arrowStyle('left')}
          >
            <i className="fa fa-chevron-left" aria-hidden="true" />
          </button>
        )}
        {canRight && (
          <button
            onClick={() => scrollByStep(1)}
            aria-label="Scroll forward in time"
            style={arrowStyle('right')}
          >
            <i className="fa fa-chevron-right" aria-hidden="true" />
          </button>
        )}
        {!following && (
          <button
            onClick={() => setFollowing(true)}
            aria-label="Snap to current time"
            style={{
              position: 'absolute',
              right: 6,
              top: -2,
              zIndex: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(107,216,136,0.15)',
              border: '1px solid rgba(107,216,136,0.45)',
              color: T.greenBright,
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 9px',
              borderRadius: 10,
              cursor: 'pointer',
            }}
          >
            <i
              className="fa fa-forward"
              aria-hidden="true"
              style={{ fontSize: 9 }}
            />
            Now
          </button>
        )}

        <div
          ref={railRef}
          onScroll={onScroll}
          className="cpl-rail"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: layout.totalWidth,
              height: railHeight,
            }}
          >
            {layout.ticks.map((tick) => (
              <React.Fragment key={tick.x}>
                <span
                  style={{
                    position: 'absolute',
                    left: tick.x + 3,
                    top: 0,
                    fontSize: 9,
                    color: T.textFaint,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tick.label}
                </span>
                <div
                  style={{
                    position: 'absolute',
                    left: tick.x,
                    top: TICKS_HEIGHT,
                    bottom: 0,
                    width: 1,
                    background: 'rgba(159,232,200,0.08)',
                  }}
                />
              </React.Fragment>
            ))}

            {layout.placed.map(({ ev, geo }) => (
              <TimelineBlock
                key={ev.session.conversationId}
                ev={ev}
                geo={geo}
                nowMs={nowMs}
                nudgeCount={
                  nudgesByConversation[ev.session.conversationId] || 0
                }
                onExpand={() => actions.openDrillIn(ev.session.conversationId)}
                onToggleRecord={toggleRecord}
              />
            ))}

            <div
              onClick={() => navigate('/User/conversations/multi-recorder')}
              role="button"
              style={{
                position: 'absolute',
                left: layout.addVisitLeft,
                top: TICKS_HEIGHT + 6,
                width: 84,
                height: LANE_HEIGHT,
                border: '1px dashed rgba(159,232,200,0.4)',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                color: T.textSecondary,
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              <i className="fa fa-plus" aria-hidden="true" />
              Add visit
            </div>

            {/* The now-line: session progress + the Pulse made visible */}
            <div
              style={{
                position: 'absolute',
                left: nowX,
                top: TICKS_HEIGHT,
                bottom: 0,
                width: 2,
                background: T.greenBright,
                boxShadow: '0 0 8px rgba(107,216,136,0.6)',
                zIndex: 3,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: nowX,
                top: 0,
                transform: 'translateX(-50%)',
                background: T.greenBright,
                color: T.greenDark,
                fontSize: 9,
                fontWeight: 700,
                padding: '0 6px',
                borderRadius: 8,
                zIndex: 3,
                whiteSpace: 'nowrap',
              }}
            >
              {fmtClock(nowMs)}
            </div>
          </div>
        </div>
      </div>
      <p
        style={{
          margin: '1px 0 0',
          textAlign: 'center',
          fontSize: 9,
          color: T.textFaint,
        }}
      >
        click a visit to record · › opens details · wheel scrubs time
      </p>
    </div>
  );
};

export default SessionRail;
